/**
 * Financial calculation utilities for rental calculator
 */

export interface CalculationInputs {
  totalMonths: number;
  baseRental: number;
  totalRental: number;
  gstRate: number; // As decimal (0.18, 0.12, 0)
  ptpmConsideration: number;
  discountingFactor: number; // As decimal (e.g., 0.095 for 9.5%)
  gstPassbackEnabled: boolean;
  disbursementAmount?: number; // Optional, will be calculated by goal seek if not provided
  disbursementDate?: string; // New field for disbursement date
  rentalCalculationBasis: 'preGstDiscounting' | 'postGstDiscounting' | 'postTdsDiscounting'; // Updated options
  securityDeposit: number;
  securityDepositPercentage: number; // As percentage value (e.g., 10 for 10%)
  processingFee: number;
  processingFeePercentage: number; // As percentage value (e.g., 1.5 for 1.5%)
  refundableSecurity: number;
  refundableSecurityPercentage: number; // As percentage value (e.g., 5 for 5%)
  rentalFrequency: 'monthly' | 'quarterly';
  dueDate?: string;
  gstPassback?:number;
  finalPTPM?:number;
  rs_ptpm?:number;
  advanceRentalEnabled: boolean;
  advanceRentalCount: number;
  advanceRentalType: 'periods' | 'percentage';
}

export interface RentalScheduleItem {
  srNo: number;
  paymentType: string;
  rentalPostTds: number;
  interest: number;
  principal: number;
  principalOutstanding: number;
  dueDate?: string; // New field for due date in schedule
  factor: number; // New field for factor (number of days)
}

export interface CalculationSummary {
  disbursementAmount: number;
  totalRentalWithGst: number;
  totalRentalPostTds: number;
  effectiveInterestRate?: number;
  totalCost: number;
  gstAmount: number;
  baseRental: number;
  securityDeposit: number;
  processingFee: number;
  totalCapitalAvailable: number;
  cashInHand: number;
  cashFlowToAstrafin: number;
  tdsOnCashFlow: number;
  effectiveCashFlow: number;
  refundableSecurity: number;
  gstPayableAfterSetoff: number;
  cashAvailable: number;
  expensesLeft: number;
  marginAvailable: number;
  marginPercentage: number;
  isNegativeMargin: boolean;
  xirr?: number;
  // Advance rental fields
  advanceRentals: number;
  gstOnAdvanceRentals: number;
  totalAdvanceRentalInclGst: number;
  advanceRentalEnabled: boolean;
}

/**
 * Enhanced rental calculation based on the new calculation basis options
 */
export const calculateEnhancedRental = (inputs: CalculationInputs): {
  finalRental: number;
  ptpmWithPassback: number;
  gstPassback: number;
  baseRental: number;
  gstAmount: number;
} => {
  const baseRental = inputs.totalRental / (1 + inputs.gstRate);
  const gstAmount = baseRental * inputs.gstRate;
  
  // GST Passback calculation (independent toggle) - fixed for quarterly
  let gstPassback = 0;
  if (inputs.gstPassbackEnabled) {
    if (inputs.rentalFrequency === 'quarterly') {
      const numberOfQuarters = Math.ceil(inputs.totalMonths / 3);
      gstPassback = gstAmount / numberOfQuarters;
    } else {
      gstPassback = gstAmount / inputs.totalMonths;
    }
  }
  
  // PTPM with GST Passback consideration
  let ptpmWithPassback = inputs.ptpmConsideration;
  if (inputs.gstPassbackEnabled) {
    ptpmWithPassback = inputs.ptpmConsideration - gstPassback;
  }
  
  // Final rental calculation based on Rental Calculation Basis
  let finalRental = 0;
  
  switch (inputs.rentalCalculationBasis) {
    case 'preGstDiscounting':
      finalRental = ptpmWithPassback;
      break;
    case 'postGstDiscounting':
      finalRental = ptpmWithPassback * (1 + inputs.gstRate);
      break;
    case 'postTdsDiscounting':
      const ptpmAfterTds = ptpmWithPassback * 0.98;
      const gstOnPtpm = ptpmWithPassback * inputs.gstRate;
      finalRental = ptpmAfterTds + gstOnPtpm;
      break;
    default:
      finalRental = ptpmWithPassback;
  }

  return {
    finalRental,
    ptpmWithPassback,
    gstPassback,
    baseRental,
    gstAmount
  };
};

export const calculateGst = (baseRental: number, gstRate: number): number => {
  return baseRental * gstRate;
};

export const calculateGstPassback = (
  gst: number,
  totalMonths: number,
  gstPassbackEnabled: boolean
): number => {
  return gstPassbackEnabled ? gst / totalMonths : 0;
};

export const calculatePtpmPostGstPassback = (
  ptpmConsideration: number,
  gstPassback: number
): number => {
  return ptpmConsideration - gstPassback;
};

export const calculateGstOnPtpm = (
  ptpmPostGstPassback: number,
  gstRate: number
): number => {
  return ptpmPostGstPassback * gstRate;
};

export const calculateTotalRentalPerMonth = (
  ptpmPostGstPassback: number,
  gstOnPtpm: number
): number => {
  return ptpmPostGstPassback + gstOnPtpm;
};

export const calculateTotalRentalPostTds = (
  ptpmPostGstPassback: number,
  gst: number
): number => {
  return ptpmPostGstPassback * 0.98 + gst;
};

export const calculateRentalBasedOnBasis = (
  ptpmPostGstPassback: number,
  rentalPostTds: number,
  basis: 'preGstDiscounting' | 'postGstDiscounting' | 'postTdsDiscounting',
  gstPassbackEnabled: boolean
): number => {
  switch (basis) {
    case 'preGstDiscounting':
      return ptpmPostGstPassback;
    case 'postGstDiscounting':
      return ptpmPostGstPassback;
    case 'postTdsDiscounting':
      return rentalPostTds;
    default:
      return rentalPostTds;
  }
};

export const daysBetween = (date1: Date, date2: Date): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.round(
    Math.abs((date1.getTime() - date2.getTime()) / oneDay)
  );
  return diffDays;
};

export const calculateInterest = (
  principalOutstanding: number,
  discountingFactor: number,
  factor: number
): number => {
  const dailyRate = discountingFactor / 365;
  return principalOutstanding * dailyRate * factor;
};

export const getNextDueDate = (currentDueDate: Date): Date => {
  const nextDate = new Date(currentDueDate);
  nextDate.setMonth(nextDate.getMonth() + 1);
  return nextDate;
};

export const calculateInterestByFrequency = (
  totalRental: number,
  discountingFactor: number,
  frequency: 'monthly' | 'quarterly'
): number => {
  const frequencyDivisor = frequency === 'monthly' ? 12 : 4;
  return (totalRental * discountingFactor) / frequencyDivisor;
};

export const generateRentalSchedule = (
  inputs: CalculationInputs
): RentalScheduleItem[] => {
  const {
    totalMonths,
    discountingFactor,
    disbursementAmount = 0,
    rentalFrequency,
    dueDate,
    disbursementDate,
  } = inputs;

  const schedule: RentalScheduleItem[] = [];
  
  const enhancedRental = calculateEnhancedRental(inputs);
  const finalRentalAmount = enhancedRental.finalRental;
  
  const paymentsPerYear = rentalFrequency === 'monthly' ? 12 : 4;
  const totalPayments = Math.ceil(totalMonths / (12 / paymentsPerYear));
  
  let principalOutstanding = disbursementAmount;
  let currentDueDate = dueDate ? new Date(dueDate) : new Date();
  const disbursementDateObj = disbursementDate ? new Date(disbursementDate) : new Date();
  let previousDate = disbursementDateObj;
  
  for (let i = 0; i < totalPayments; i++) {
    const srNo = i + 1;
    const paymentType = `Advance Rental - ${rentalFrequency === 'monthly' ? 'Monthly' : 'Quarterly'}`;
    
    let factor: number;
    if (i === 0) {
      factor = daysBetween(currentDueDate, disbursementDateObj);
    } else {
      factor = daysBetween(currentDueDate, previousDate);
    }
    
    const interest = calculateInterest(principalOutstanding, discountingFactor, factor);
    const principal = finalRentalAmount - interest;
    principalOutstanding -= principal;
    
    const formattedDueDate = currentDueDate.toISOString().split('T')[0];

    schedule.push({
      srNo,
      paymentType,
      rentalPostTds: finalRentalAmount,
      interest,
      principal,
      principalOutstanding,
      dueDate: formattedDueDate,
      factor,
    });
    
    previousDate = new Date(currentDueDate);
    
    if (rentalFrequency === 'monthly') {
      currentDueDate.setMonth(currentDueDate.getMonth() + 1);
    } else {
      currentDueDate.setMonth(currentDueDate.getMonth() + 3);
    }
  }
  
  return schedule;
};

function calculateIRR(cashFlows: number[]): number | undefined {
  const guess = 0.1;
  const maxIterations = 1000;
  const precision = 1e-7;
  let rate = guess;

  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    let derivative = 0;

    for (let t = 0; t < cashFlows.length; t++) {
      npv += cashFlows[t] / Math.pow(1 + rate, t);
      derivative += -t * cashFlows[t] / Math.pow(1 + rate, t + 1);
    }

    const newRate = rate - npv / derivative;
    if (Math.abs(newRate - rate) < precision) return rate * 100;
    rate = newRate;
  }

  return undefined;
}

export const calculateXirr = (
  cashFlows: number[],
  dates: Date[],
  guess: number = 0.1
): number => {
  const irrResult = calculateIRR(cashFlows);
  
  if (irrResult === undefined) {
    throw new Error('XIRR calculation failed to converge');
  }
  
  return irrResult;
};

export const calculateSummary = (
  inputs: CalculationInputs,
  schedule: RentalScheduleItem[]
): CalculationSummary => {
  const enhancedRental = calculateEnhancedRental(inputs);
  
  const totalRentalWithGst = enhancedRental.finalRental * inputs.totalMonths;
  
  const totalRentalPostTds = schedule.reduce(
    (sum, item) => sum + item.rentalPostTds,
    0
  );
  
  const securityDeposit = (inputs.securityDepositPercentage / 100) * inputs.totalRental;
  const processingFee = (inputs.processingFeePercentage / 100) * inputs.totalRental;
  const refundableSecurity = (inputs.refundableSecurityPercentage / 100) * inputs.totalRental;
  
  // Advance rental calculations (always based on pre-GST rental = ptpmWithPassback)
  const advanceRentalEnabled = inputs.advanceRentalEnabled || false;
  const advanceRentalCount = inputs.advanceRentalCount || 0;
  let advanceRentals = 0;
  if (advanceRentalEnabled) {
    if (inputs.advanceRentalType === 'percentage') {
      advanceRentals = inputs.totalRental * (advanceRentalCount / 100);
    } else {
      advanceRentals = enhancedRental.ptpmWithPassback * advanceRentalCount;
    }
  }
  const gstOnAdvanceRentals = advanceRentalEnabled ? advanceRentals * inputs.gstRate : 0;
  const totalAdvanceRentalInclGst = advanceRentals + gstOnAdvanceRentals;
  
  // Total Capital Available includes disbursement + SD + PF + Advance Rental (if enabled)
  let totalCapitalAvailable = (inputs.disbursementAmount || 0) + securityDeposit + processingFee;
  if (advanceRentalEnabled) {
    totalCapitalAvailable += totalAdvanceRentalInclGst;
  }

  const advanceRentalTds = advanceRentals * 0.02;
  
  // Cash in Hand: F = E - A - Adv. rental TDS
  let cashInHand = totalCapitalAvailable - inputs.totalRental;
  if (advanceRentalEnabled) {
    cashInHand -= advanceRentalTds;
  }
  
  const gstAmountOnTotalRental = inputs.totalRental * inputs.gstRate;
  
  const finalPtpmWithPassbackCalc = enhancedRental.ptpmWithPassback;
  const gstOnFinalPtpm = finalPtpmWithPassbackCalc * inputs.gstRate;
  const paymentsPerYear = inputs.rentalFrequency === 'monthly' ? inputs.totalMonths : inputs.totalMonths / 3;
  const backendCashFlowToAstrafin = gstOnFinalPtpm * paymentsPerYear;
  
  let cashFlowToAstrafin = 0;
  switch (inputs.rentalCalculationBasis) {
    case 'preGstDiscounting':
      cashFlowToAstrafin = backendCashFlowToAstrafin;
      break;
    case 'postGstDiscounting':
    case 'postTdsDiscounting':
      cashFlowToAstrafin = 0;
      break;
  }
  
  let tdsOnCashFlow = 0;
  switch (inputs.rentalCalculationBasis) {
    case 'preGstDiscounting':
      tdsOnCashFlow = (enhancedRental.finalRental * 0.02) * paymentsPerYear;
      break;
    case 'postGstDiscounting':
      tdsOnCashFlow = (enhancedRental.ptpmWithPassback * 0.02) * paymentsPerYear;
      break;
    case 'postTdsDiscounting':
      tdsOnCashFlow = 0;
      break;
  }
  
  const effectiveCashFlow = cashFlowToAstrafin - tdsOnCashFlow;
  
  const gstAmount = inputs.totalRental / (1 + inputs.gstRate) * inputs.gstRate;
  // GST Payable: when advance rental enabled, add GST on Advance Rentals
  let gstPayableAfterSetoff = backendCashFlowToAstrafin - gstAmount;
  if (advanceRentalEnabled) {
    gstPayableAfterSetoff += gstOnAdvanceRentals;
  }
  
  // Cash Available: when advance rental enabled = cashInHand + effectiveCashFlow + totalAdvanceRentalInclGst - advanceRentals*2%
  let cashAvailable: number;
  if (advanceRentalEnabled) {
    cashAvailable = cashInHand + effectiveCashFlow;
  } else {
    cashAvailable = cashInHand + effectiveCashFlow;
  }
  const expensesLeft = refundableSecurity + gstPayableAfterSetoff;
  
  const marginAvailable = cashAvailable - expensesLeft;
  const marginPercentage = (marginAvailable / inputs.totalRental) * 100;
  const isNegativeMargin = marginPercentage < 0;
  
  let effectiveInterestRate: number | undefined;
  try {
    const irrCashFlows: number[] = [];
    irrCashFlows.push(-inputs.totalRental);
    schedule.forEach(() => {
      irrCashFlows.push(enhancedRental.finalRental);
    });
    effectiveInterestRate = calculateIRR(irrCashFlows);
  } catch (error) {
    console.error('IRR calculation error:', error);
    effectiveInterestRate = undefined;
  }
  
  let xirr: number | undefined;
  try {
    const cashFlows: number[] = [];
    const dates: Date[] = [];
    const startDate = inputs.disbursementDate ? new Date(inputs.disbursementDate) : new Date();
    cashFlows.push(-inputs.totalRental);
    dates.push(startDate);
    schedule.forEach((item) => {
      if (item.dueDate) {
        cashFlows.push(enhancedRental.finalRental);
        dates.push(new Date(item.dueDate));
      }
    });
    if (cashFlows.length > 1) {
      xirr = calculateXirr(cashFlows, dates);
    }
  } catch (error) {
    console.error('XIRR calculation error:', error);
    xirr = undefined;
  }
  
  return {
    disbursementAmount: inputs.disbursementAmount!,
    totalRentalWithGst,
    totalRentalPostTds,
    effectiveInterestRate,
    totalCost: inputs.totalRental,
    gstAmount: enhancedRental.gstAmount,
    baseRental: enhancedRental.baseRental,
    securityDeposit,
    processingFee,
    totalCapitalAvailable,
    cashInHand,
    cashFlowToAstrafin,
    tdsOnCashFlow,
    effectiveCashFlow,
    refundableSecurity,
    gstPayableAfterSetoff,
    cashAvailable,
    expensesLeft,
    marginAvailable,
    marginPercentage,
    isNegativeMargin,
    xirr,
    advanceRentals,
    gstOnAdvanceRentals,
    totalAdvanceRentalInclGst,
    advanceRentalEnabled,
  };
};